import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Card,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Chip,
  alpha,
} from '@mui/material';
import { CloudUpload, Delete, OpenInNew } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { Video } from '../../types';

interface AdminVideosProps {
  userId: string;
}

const AdminVideos: React.FC<AdminVideosProps> = ({ userId }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  useEffect(() => {
    fetchVideos();
  }, [userId]);

  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      const data = await api.getUserVideos(userId);
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        try {
          setIsUploading(true);
          const formData = new FormData();
          formData.append('video', acceptedFiles[0]);
          await api.uploadUserVideo(userId, formData);
          toast.success('Video uploaded successfully!');
          fetchVideos();
        } catch (error: any) {
          console.error('Upload error:', error);
          toast.error(error?.response?.data?.error || 'Failed to upload video');
        } finally {
          setIsUploading(false);
        }
      }
    },
  });

  const handleDeleteClick = (videoId: string) => {
    setSelectedVideoId(videoId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedVideoId) return;
    try {
      await api.adminDeleteUserVideo(userId, selectedVideoId);
      setVideos((prev) => prev.filter((video) => video._id !== selectedVideoId));
      toast.success('Video deleted successfully');
    } catch (error) {
      toast.error('Failed to delete video');
      console.error('Delete error:', error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedVideoId(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        '@keyframes mediaSoftIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          p: { xs: 2.25, sm: 3.5 },
          mb: { xs: 1.75, sm: 2.5 },
          textAlign: 'center',
          borderRadius: { xs: 3, sm: 4 },
          cursor: isUploading ? 'wait' : 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : alpha('#f48fb1', 0.45),
          background: isDragActive
            ? alpha('#f48fb1', 0.12)
            : `linear-gradient(165deg, #fff 0%, ${alpha('#ffc1e3', 0.18)} 100%)`,
          transition: 'all 0.25s ease',
          animation: 'mediaSoftIn 0.4s ease both',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: alpha('#f48fb1', 0.08),
          },
        }}
      >
        <input {...getInputProps()} disabled={isUploading} />
        <Box
          sx={{
            width: { xs: 52, sm: 64 },
            height: { xs: 52, sm: 64 },
            mx: 'auto',
            mb: 1.25,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha('#f48fb1', 0.16),
            color: 'primary.dark',
          }}
        >
          <CloudUpload sx={{ fontSize: { xs: 28, sm: 34 } }} />
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            lineHeight: 1.35,
            color: '#2d2430',
            px: 0.5,
          }}
        >
          {isDragActive
            ? 'Thả video vào đây'
            : 'Kéo thả video vào đây, hoặc bấm để chọn'}
        </Typography>
        <Chip
          size="small"
          label="MP4 / MOV / AVI · tối đa 50MB"
          sx={{
            mt: 1.25,
            fontWeight: 700,
            fontSize: '0.7rem',
            bgcolor: alpha('#f48fb1', 0.12),
            color: 'primary.dark',
          }}
        />
        {isUploading && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={26} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Đang upload…
            </Typography>
          </Box>
        )}
      </Paper>

      {videos.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography color="text.secondary">Chưa có video nào</Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: { xs: 1.25, sm: 2 },
          }}
        >
          {videos.map((video, index) => (
            <Card
              key={video._id}
              elevation={0}
              sx={{
                borderRadius: { xs: 2.5, sm: 3 },
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                minWidth: 0,
                boxShadow: `0 6px 18px ${alpha('#bf5f82', 0.08)}`,
                animation: 'mediaSoftIn 0.4s ease both',
                animationDelay: `${Math.min(index, 10) * 0.04}s`,
              }}
            >
              <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: '#111' }}>
                <video
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  src={video.url}
                  controls
                  preload="metadata"
                />
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(video._id)}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 2,
                    bgcolor: alpha('#fff', 0.92),
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    '&:hover': { bgcolor: '#fff', color: 'error.main' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ p: { xs: 1, sm: 1.25 } }}>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    color: '#2d2430',
                  }}
                  title={video.name}
                >
                  {video.name || 'Video'}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={0.5}
                  sx={{ mt: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                  >
                    {new Date(video.createdAt).toLocaleDateString('vi-VN')}
                  </Typography>
                  <Button
                    size="small"
                    href={video.url}
                    target="_blank"
                    startIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.15,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      color: 'primary.dark',
                    }}
                  >
                    Mở
                  </Button>
                </Stack>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Xóa video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc muốn xóa video này? Hành động không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Hủy
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminVideos;
